import { produce } from 'immer'

// Mirrors the flow run's own states. `starting` is client-only: launching is a POST, and the button should read
// as busy from the click rather than from the answer.
export const RUN_STATUS = {
  STARTING: 'starting',
  QUEUED: 'queued',
  RUNNING: 'running',
  DONE: 'done',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
}

export const ACTIVE_STATUSES = [RUN_STATUS.STARTING, RUN_STATUS.QUEUED, RUN_STATUS.RUNNING]
export const POLLABLE_STATUSES = [RUN_STATUS.QUEUED, RUN_STATUS.RUNNING]

const initialState = {
  orgsLoading: false,
  orgs: [],
  orgsError: null,

  // Keyed by org id: { loading, error, scorecards: [] }
  byOrg: {},

  // Keyed by scorecard config id:
  // { status, flowRunId, startedAt, subjectKeys, results, resultsLoading, applying, error }
  runs: {},
}

const emptyRun = {
  status: null,
  flowRunId: null,
  startedAt: null,
  // Which prompts the run covers. ⚠ NULL is "nobody has told us yet" and an empty LIST is "all of them" —
  // two different things, because the page has not polled this scorecard's run when it first renders and
  // reading unknown as all-of-them marks every row as being tested.
  subjectKeys: null,
  results: null,
  // Which flow run the results in hand describe. A re-test of one prompt is a NEW flow run, and without
  // this the results already loaded would satisfy "we have results" for the rest of the session — so the
  // page would keep showing the old measurement after the new one finished.
  resultsFor: null,
  resultsLoading: false,
  applying: false,
  error: null,
}

export const SET_ORGS_LOADING = 'costimizer/SET_ORGS_LOADING'
export const SET_ORGS = 'costimizer/SET_ORGS'
export const SET_ORGS_ERROR = 'costimizer/SET_ORGS_ERROR'
export const SET_ORG_LOADING = 'costimizer/SET_ORG_LOADING'
export const SET_ORG_SCORECARDS = 'costimizer/SET_ORG_SCORECARDS'
export const SET_ORG_ERROR = 'costimizer/SET_ORG_ERROR'
export const SET_RUN = 'costimizer/SET_RUN'
export const SET_RUN_RESULTS = 'costimizer/SET_RUN_RESULTS'
export const SET_RESULTS_LOADING = 'costimizer/SET_RESULTS_LOADING'
export const SET_APPLYING = 'costimizer/SET_APPLYING'
export const SET_RUN_ERROR = 'costimizer/SET_RUN_ERROR'

export const setOrgsLoading = (loading) => ({ type: SET_ORGS_LOADING, payload: loading })
export const setOrgs = (orgs) => ({ type: SET_ORGS, payload: orgs })
export const setOrgsError = (error) => ({ type: SET_ORGS_ERROR, payload: error })
export const setOrgLoading = (organizationId, loading) => ({
  type: SET_ORG_LOADING,
  payload: { organizationId, loading },
})
export const setOrgScorecards = (organizationId, scorecards) => ({
  type: SET_ORG_SCORECARDS,
  payload: { organizationId, scorecards },
})
export const setOrgError = (organizationId, error) => ({
  type: SET_ORG_ERROR,
  payload: { organizationId, error },
})
export const setRun = (scorecardConfigId, run) => ({
  type: SET_RUN,
  payload: { scorecardConfigId, run },
})
export const setRunResults = (scorecardConfigId, results, flowRunId) => ({
  type: SET_RUN_RESULTS,
  payload: { scorecardConfigId, results, flowRunId },
})
export const setResultsLoading = (scorecardConfigId, loading) => ({
  type: SET_RESULTS_LOADING,
  payload: { scorecardConfigId, loading },
})
export const setApplying = (scorecardConfigId, applying) => ({
  type: SET_APPLYING,
  payload: { scorecardConfigId, applying },
})
export const setRunError = (scorecardConfigId, error) => ({
  type: SET_RUN_ERROR,
  payload: { scorecardConfigId, error },
})

export const selectOrgs = (state) => state.costimizer.orgs
export const selectOrgsLoading = (state) => state.costimizer.orgsLoading
export const selectOrgsError = (state) => state.costimizer.orgsError
export const selectOrg = (organizationId) => (state) =>
  state.costimizer.byOrg[organizationId] ?? { loading: false, error: null, scorecards: [] }
export const selectRun = (scorecardConfigId) => (state) =>
  state.costimizer.runs[scorecardConfigId] ?? emptyRun

export default (state = initialState, action = {}) =>
  produce(state, (newState) => {
    const withRun = (scorecardConfigId) => {
      if (!newState.runs[scorecardConfigId]) {
        newState.runs[scorecardConfigId] = { ...emptyRun }
      }

      return newState.runs[scorecardConfigId]
    }

    switch (action.type) {
      case SET_ORGS_LOADING:
        newState.orgsLoading = action.payload
        break
      case SET_ORGS:
        newState.orgs = action.payload
        newState.orgsLoading = false
        newState.orgsError = null
        break
      case SET_ORGS_ERROR:
        newState.orgsError = action.payload
        newState.orgsLoading = false
        break
      case SET_ORG_LOADING: {
        const { organizationId, loading } = action.payload
        if (!newState.byOrg[organizationId]) {
          newState.byOrg[organizationId] = { loading: false, error: null, scorecards: [] }
        }
        newState.byOrg[organizationId].loading = loading
        break
      }
      case SET_ORG_SCORECARDS: {
        const { organizationId, scorecards } = action.payload
        newState.byOrg[organizationId] = { loading: false, error: null, scorecards }
        break
      }
      case SET_ORG_ERROR: {
        const { organizationId, error } = action.payload
        if (!newState.byOrg[organizationId]) {
          newState.byOrg[organizationId] = { loading: false, error: null, scorecards: [] }
        }
        newState.byOrg[organizationId].error = error
        newState.byOrg[organizationId].loading = false
        break
      }
      case SET_RUN: {
        const { scorecardConfigId, run } = action.payload
        const entry = withRun(scorecardConfigId)
        Object.assign(entry, run)
        entry.error = null
        break
      }
      case SET_RUN_RESULTS: {
        const { scorecardConfigId, results, flowRunId } = action.payload
        const entry = withRun(scorecardConfigId)
        entry.results = results
        entry.resultsFor = flowRunId ?? null
        entry.resultsLoading = false
        break
      }
      case SET_RESULTS_LOADING: {
        const { scorecardConfigId, loading } = action.payload
        withRun(scorecardConfigId).resultsLoading = loading
        break
      }
      case SET_APPLYING: {
        const { scorecardConfigId, applying } = action.payload
        withRun(scorecardConfigId).applying = applying
        break
      }
      case SET_RUN_ERROR: {
        const { scorecardConfigId, error } = action.payload
        const entry = withRun(scorecardConfigId)
        entry.error = error
        entry.applying = false
        entry.resultsLoading = false
        break
      }
      default:
        break
    }
  })
